import { Application, Container } from 'pixi.js'
import { markGameReady, setLoadingProgress } from './boot/loadingProgress'
import { logger } from './framework/core/Logger'
import { EventBus } from './framework/events/EventBus'
import type { Resizable } from './framework/render/design'
import { StageScaler } from './framework/render/StageScaler'
import { AudioManager, loadAudio } from './framework/audio/AudioManager'
import { loadBottomBarTextures } from './game/assets/BottomBarTextures'
import { loadMultiplierTextures } from './game/assets/MultiplierTextures'
import { loadSymbolSpines } from './game/assets/SymbolSpine'
import { loadSymbolTextures } from './game/assets/SymbolTextures'
import { loadUiTextures } from './game/assets/UiTextures'
import { loadWinSpine } from './game/assets/WinSpine'
import { FeatureOverlay } from './game/ui/FeatureOverlay'
import { BackgroundView } from './game/view/BackgroundView'
import { SpineSceneView, loadSceneSpines } from './game/view/SpineSceneView'
import { createRuntimeConfig } from './game/config/gameConfig'
import type { GameEventMap } from './game/events/GameEvents'
import { GameController } from './game/GameController'
import { GameServerClient } from './framework/net/GameServerClient'
import { toClientBalance } from './framework/net/balance'
import type { RuntimeConfig } from './game/config/gameConfig'
import { MockSpinService } from './game/net/MockSpinService'
import { RemoteSpinService } from './game/net/RemoteSpinService'
import type { SpinService } from './game/net/SpinService'
import { PlayerState } from './game/state/PlayerState'
import { SideWheelView } from './game/bonus/SideWheelView'
import { BottomBarView } from './game/ui/BottomBarView'
import { ExtraBetToggle } from './game/ui/ExtraBetToggle'
import { ReelsView } from './game/view/ReelsView'
import { WinCelebration } from './game/view/WinCelebration'
import { SOURCE_VERSION } from './game/constants'

const FIXED_STEP = 1 / 60
const MAX_STEPS_PER_FRAME = 5

// Composition root — 取代 Unity GameLifetimeScope（VContainer）。
async function bootstrap(): Promise<void> {
  const mount = document.getElementById('app')
  if (mount === null) {
    throw new Error('找不到掛載節點 #app')
  }

  const app = new Application()
  await app.init({
    background: '#0b0b14',
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: window.devicePixelRatio || 1,
  })
  mount.appendChild(app.canvas)
  setLoadingProgress(0.1) // 渲染器就緒

  // 先載入美術（沿用 Unity 資源：符號 + 倍數幣 + 背景/框/Logo + 操作列），再建立畫面
  await Promise.all([
    loadSymbolTextures(),
    loadMultiplierTextures(),
    loadUiTextures(),
    loadBottomBarTextures(),
  ])
  setLoadingProgress(0.55) // 美術貼圖完成
  loadAudio() // 音效（沿用 Unity 音效檔，背景非同步預載）
  try {
    await Promise.all([loadSymbolSpines(), loadWinSpine()]) // 符號中獎 + 贏分慶祝 Spine
  } catch (error) {
    logger.error('Spine 載入失敗', error)
  }
  setLoadingProgress(0.8) // 符號/贏分 Spine 完成

  // ── 依賴組裝 ──
  const config = createRuntimeConfig()
  const bus = new EventBus<GameEventMap>()
  new AudioManager(bus) // 訂閱事件並播放音效
  const session = await createSpinService(config)
  setLoadingProgress(0.85) // Spin 服務就緒（離線 Mock 或已連後端）
  const spinService = session.service
  // 連真後端時用伺服器餘額/押注區間；離線 Mock 時為 undefined → PlayerState 採預設值。
  const player = new PlayerState(session.balance, session.betOptions)

  const background = new BackgroundView()
  const reels = new ReelsView(config, () => player.extraBet)
  const bottomBar = new BottomBarView(bus, player)
  const winCelebration = new WinCelebration()
  const feature = new FeatureOverlay()
  const sideWheel = new SideWheelView(reels)
  const extraBet = new ExtraBetToggle(bus, player, reels)

  // ExtraBet 切換：啟用時倍數幣飛入第四輪，待升級幣抵達後才切換輪帶顯示（X15 出現）；停用則直接切換
  bus.on('extraBetChanged', ({ active }) => {
    if (active) {
      void reels.playExtraBetFx().then(() => reels.refreshMultiplierStrip())
    } else {
      reels.refreshMultiplierStrip()
    }
  })

  // 嘗試載入 Spine 場景（櫻花背景 + 老虎角色 + Logo）；失敗則退回漸層底色
  let spineScene: SpineSceneView | null = null
  try {
    await loadSceneSpines()
    spineScene = new SpineSceneView()
  } catch (error) {
    logger.error('Spine 場景載入失敗，改用漸層背景', error)
  }
  setLoadingProgress(0.95) // Spine 場景完成

  // 圖層順序：漸層底 → Spine 場景 → 盤面 → HUD/操作列 → 中獎/Bonus 覆蓋層
  const layers: Container[] = [background]
  if (spineScene !== null) layers.push(spineScene)
  layers.push(sideWheel, reels, bottomBar, extraBet, winCelebration, feature)
  app.stage.addChild(...layers)

  const controller = new GameController({
    bus,
    player,
    spinService,
    reels,
    win: winCelebration,
    bonus: sideWheel,
    feature,
  })

  // 初始靜態盤面（本機隨機；真正的旋轉在玩家按 SPIN 時才向後端請求）
  reels.setStatic(randomInitialBoard(config))

  // 自適應佈局（reels 需先於 sideWheel 算出主盤框矩形，供立輪定位）
  const resizables: Resizable[] = [background, reels, sideWheel, extraBet, bottomBar, winCelebration, feature]
  if (spineScene !== null) resizables.splice(1, 0, spineScene)
  const scaler = new StageScaler(app, resizables)
  scaler.layout()
  markGameReady() // 主畫面已組裝並完成首次佈局 → 通知讀取疊層補滿並淡出

  // 固定步長遊戲迴圈（轉輪物理）— 取代 Unity FixedUpdate
  let accumulator = 0
  app.ticker.add((ticker) => {
    accumulator += ticker.deltaMS / 1000
    let steps = 0
    while (accumulator >= FIXED_STEP && steps < MAX_STEPS_PER_FRAME) {
      reels.update(FIXED_STEP)
      accumulator -= FIXED_STEP
      steps += 1
    }
  })

  logger.info(`FaFaCat 啟動完成（移植來源 ${SOURCE_VERSION}，狀態 ${controller.currentState}）`)

  // Dev 診斷掛載點：在 console 以 `await __spin()` 直接送一次旋轉，回傳精簡結果（驗證後端連線）。
  if (import.meta.env.DEV) {
    ;(window as unknown as Record<string, unknown>).__spin = async (buyType = 0): Promise<unknown> => {
      const result = await spinService.spin({ totalBet: player.totalBet, buyType })
      return {
        code: result.code,
        totalBet: player.totalBet,
        board: result.mgResult.mgSymbol,
        lineCount: result.mgResult.mgLineCount,
        lineWin: result.mgResult.mgLineWin.map((w) => String(w)),
        multiplier: result.mgResult.mgMultiplier,
        totalWin: String(result.totalWin),
        serverBalance: result.serverBalance === undefined ? null : String(result.serverBalance),
        gameMode: result.gameMode,
      }
    }
    // 與 SPIN 按鈕完全相同的事件路徑（經 GameController 完整流程：扣款→轉輪→中獎→餘額校正）。
    ;(window as unknown as Record<string, unknown>).__uiSpin = (): void =>
      bus.emit('spinRequested', { buyType: player.extraBet ? 1 : 0 })
    ;(window as unknown as Record<string, unknown>).__player = (): unknown => ({
      balance: player.balance,
      bet: player.bet,
    })
    // 讀目前畫面實際顯示的符號盤面（各符號轉輪 finalIds），用來比對後端盤面。
    ;(window as unknown as Record<string, unknown>).__board = (): unknown => reels.displayedBoard
    // 乘倍演出 demo（直接驅動表現層，免依賴隨機中獎）：基礎計數 → 倍數幣飛入 → ×N 兩段計數。
    ;(window as unknown as Record<string, unknown>).__demoMult = async (
      multiplier = 5,
      base = 1234,
    ): Promise<void> => {
      await winCelebration.showWin(base, 0.5, 100)
      await reels.playMultiplierApplyFx(multiplier, false)
      bus.emit('multiplierApplied', { multiplier })
      await winCelebration.applyMultiplier(base, base * multiplier, multiplier, 0.7)
      winCelebration.hide()
    }
    // 大獎報獎 demo：與 GameController 相同順序 —— 先發事件播報獎音樂，再 await ani_win Spine 報獎演出（兩者同時起）。
    ;(window as unknown as Record<string, unknown>).__demoBigWin = async (
      tier = 'MEGA WIN',
      amount = 88888,
    ): Promise<void> => {
      bus.emit('bigWinShown', { tier, amount })
      await winCelebration.showBigWin(tier, amount)
      winCelebration.hide()
    }
  }
}

// 連線結果：Spin 服務 + 伺服器提供的玩家設定（餘額 / 合法押注區間，皆為伺服器整數單位）。
// 連 Mock 時不帶餘額/押注區間，由 PlayerState 預設值帶入。
interface SpinSession {
  readonly service: SpinService
  readonly balance?: number
  readonly betOptions?: readonly number[]
}

// 預設使用離線 Mock；若設定 VITE_SERVER_URL 則改連真實後端（失敗時回退 Mock）。
async function createSpinService(config: RuntimeConfig): Promise<SpinSession> {
  const url = import.meta.env['VITE_SERVER_URL']
  if (typeof url === 'string' && url.length > 0) {
    try {
      const client = new GameServerClient()
      await client.connect(url, { gameName: 'Slot001', currency: 'PHP' })
      // 用後端權威的 BetRange 作為押注選項、伺服器餘額取代預設值（皆 ÷10000 轉顯示單位），
      // 否則送出的 TotalBet 不在允許區間會被拒（InvalidBetAmount, code=13）。
      const betOptions = client.betRange.map((value) => toClientBalance(value))
      logger.info(`已連線後端：${url}（餘額 ${client.balance} ${client.currency}）`)
      return {
        service: new RemoteSpinService(client),
        balance: toClientBalance(client.balance),
        ...(betOptions.length > 0 ? { betOptions } : {}),
      }
    } catch (error) {
      logger.error('連線後端失敗，改用離線 Mock', error)
    }
  }
  return {
    service: new MockSpinService({
      reelCount: config.reelCount,
      rowCount: config.rowCount,
      paylines: config.paylines,
    }),
  }
}

// 初始顯示盤面：本機隨機產生（不在開機時對後端送出真實 spin，避免真扣款並防止單點失敗中斷啟動）。
function randomInitialBoard(config: RuntimeConfig): number[][] {
  const symbolCount = config.symbolNames.length
  return Array.from({ length: config.reelCount }, () =>
    Array.from({ length: config.rowCount }, () => Math.floor(Math.random() * symbolCount)),
  )
}

bootstrap().catch((error: unknown) => {
  logger.error('啟動失敗', error)
})
